/* tslint:disable:variable-name */

import { ACCOUNT_EXCLUSION_TYPES, DEFAULT_SEVERITIES } from 'appConstants';

import { MiscUtils, NewTicketUtils } from 'services';
import { Account, Category, Subcategory } from 'types';

const AccountUtils = {
  /** checks if draft severity is valid */
  checkForValidSeverity: (
    draftAccount: Account,
    draftSeverity: string,
  ): boolean =>
    AccountUtils.getSeveritiesLength(draftAccount) >= 1 &&
    draftAccount.severities.some(
      (item) => item.toLowerCase() === draftSeverity,
    ),
  /** checks if draft selected product is valid */
  checkForValidProduct: (
    draftCategory: Category,
    draftAccount: Account,
    draftSubCategory: string,
    isDeviceAdmin: boolean,
  ): boolean => {
    const subCategories = NewTicketUtils.getSubcategories(
      draftCategory,
      draftAccount,
      isDeviceAdmin,
    );
    return subCategories.some((item) => item.name === draftSubCategory);
  },
  /** Return an account's default severity */
  defaultSeverity: (account?: Account) => {
    if (!account) {
      return '';
    }

    const default_Severity = 'Normal';

    let severity = DEFAULT_SEVERITIES[account.type?.id] ?? default_Severity;
    // Managed GCP is a nested object keyed on service levels
    if (typeof severity === 'object') {
      const serviceLevel = severity[account.serviceLevel.toLowerCase()];

      severity = serviceLevel ? serviceLevel : default_Severity;
    }

    // Use first severity if default severity not listed in severity array
    if (
      Array.isArray(account.severities) &&
      !account.severities.includes(severity)
    ) {
      severity = account.severities[0];
    }

    return severity ? severity.toLowerCase() : '';
  },

  /** Find account in array that has matching id provided */
  findById: (accounts: Account[], id: string) =>
    accounts.find((item) => item.id === id) || ({} as Account),

  /** Return formatted version of account info */
  formatAccount: ({ name, number, serviceLevel, type }: Account) =>
    name ? `${name} (${number}) | ${serviceLevel} ${type.name}` : '',

  /** Return boolean indicating if user has admin on account */
  hasAdmin: ({ permission }: Account) => permission === 'admin',

  /** Return boolean indicating if user has device-admin on account */
  isDeviceAdmin: ({ permission }: Account) => permission === 'device-admin',

  /** Return boolean indicating if user has ticket-admin on account */
  isTicketAdmin: ({ permission }: Account) => permission === 'ticket-admin',

  /** Return length of severities of the given account */
  getSeveritiesLength: (account: Account = {} as Account): number =>
    Array.isArray(account.severities) ? account.severities.length : 0,

  /** Return boolean indicating if account is type 'Dedicated */
  isDedicated: (account: Account = {} as Account) =>
    MiscUtils.get<string>(account, 'type.name', '') === 'Dedicated',

  isItilCustomer: (account: Account = {} as Account) =>
    MiscUtils.get<string>(account, 'ServiceManagement', '') === 'ITIL', 

  /** Return boolean indicating if account includes a tag of  'rbu */
  isRbu: (account: Account = {} as Account) =>
    Array.isArray(account.tags) && account.tags.includes('product:rbu'),

  /** Return boolean indicating if account includes a tag of 'ras' */
  isRas: (account: Account = {} as Account) =>
    Array.isArray(account.tags) && account.tags.includes('product:ras'),

  /** Return boolean indicating if account tags include in disabled category tags  */
  checkAccountTags: (disabledCategoryTags: string[], accountTags: string[]) => {
    const filteredAccount = accountTags.filter(
      (tag: string) => tag.indexOf('serviceBlock:') > -1,
    );
    if (filteredAccount.length > 0) {
      return filteredAccount.every((tag: string) =>
        disabledCategoryTags.includes(tag),
      );
    }
    return false;
  },

  /**
   * Checks if domainId has 'dedicated:'.
   * If exists replace with 'hybrid:',
   * Else return same domainId
   */
  renameDedicatedDomainId: (domainId: string) =>
    domainId.includes('dedicated:')
      ? domainId.replace('dedicated:', 'hybrid:')
      : domainId,

  /** Return boolean indicating if account includes tags prefixed with 'serviceBlock:' **/
  hasServiceBlock: (account: Account = {} as Account) =>
    Array.isArray(account.tags) &&
    account.tags.some((tag) => tag.includes('serviceBlock:')),

  /** Create hash of account fields for text search */
  searchHash: (account: Account) =>
    `${account.name} ${account.number} ${account.type.name} ${
      account.serviceLevel
    }`.toLowerCase(),

  /** Return account with number and capitalized service level */
  transform: (account: Account) => ({
    ...account,
    // strip number from id (faws:1234 => 1234)
    number: account.id ? account.id.substr(account.id.indexOf(':') + 1) : '',
    serviceLevel: MiscUtils.capitalize(account.serviceLevel || ''),
  }),

  /** Return boolean indicating if severity is relevant for given account  */
  usesSeverity: ({ type, serviceLevel }: Account) =>
    !!serviceLevel &&
    type.id !== 'cloud' &&
    serviceLevel !== ACCOUNT_EXCLUSION_TYPES[type.id],

  filterCategoriesForDeviceAdmin: (categories: Category[]) => {
    return categories.filter((category: Category, index: number) => {
      let isDeviceSupportedByCategory: boolean = false;
      category.subcategories.forEach((subcategory: Subcategory) => {
        isDeviceSupportedByCategory =
          (subcategory.tags &&
            subcategory.tags.hybrid &&
            subcategory.tags.hybrid.indexOf('devices:supported') > -1) ||
          isDeviceSupportedByCategory;
      });
      return isDeviceSupportedByCategory;
    });
  },

  /** Check if the selectedAccount has any Admin role */
  isAdmin: (selectedAccount: string, accounts: Account[]) => {
    const filteredAccount = accounts.filter(({ id }) => id === selectedAccount);

    return (
      filteredAccount.some(AccountUtils.hasAdmin) ||
      filteredAccount.some(AccountUtils.isDeviceAdmin) ||
      filteredAccount.some(AccountUtils.isTicketAdmin)
    );
  },
};

export { AccountUtils };
